import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { useMessageBus } from "@jti/fe-message-bus";
import { useDimension } from "../../../../hooks/useDimension";
import { useConfig } from "../../../../providers/useConfig";
import { StoreLocatorEvents } from "./useYandexMapsStoreLocator.types";
import { createSuggestionItems, DESKTOP_BREAKDOWN, getClustererCircle, getCustomMarker, getUserLocationMarker, getUserLocationPopup, mapStoresPoints, MARKER_SOURCE, updateDropdownContainer, updateStoreElements, } from "./utils";
import styles from "./yandexStyles.module.scss";
export const useYandexMapsStoreLocator = ({ defaultLat = "0", defaultLng = "0", defaultZoom = 10, activeStoreId, handleSelectStore, handleLoadFlotiqStores, defaultStoreIcon, storeIcons, isSearchVisible, }) => {
    const messageBus = useMessageBus();
    const { width } = useDimension();
    const isDesktopView = width > DESKTOP_BREAKDOWN;
    const { mapProviderExternalJs } = useConfig();
    const inputRef = useRef(null);
    const yandexMapRef = useRef(null);
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const [isSearchResultPending, setIsSearchResultPending] = useState(false);
    const [markers, setMarkers] = useState([]);
    const [clusters, setClusters] = useState(null);
    const [isMapLoading, setIsMapLoading] = useState(false);
    const [isMapError, setIsMapError] = useState(false);
    const handleLoadMarkers = async (stores) => {
        if (!yandexMapRef.current) {
            return;
        }
        const { YMapMarker } = ymaps3;
        setMarkers(stores);
        let clusterer;
        const points = mapStoresPoints(stores);
        const createMarker = (feature) => {
            const { id, geometry: { coordinates }, properties: { store }, } = feature;
            const newMarker = new YMapMarker({ coordinates: coordinates, id: id, source: MARKER_SOURCE, zIndex: 1 }, getCustomMarker(store, storeIcons, defaultStoreIcon));
            return newMarker;
        };
        const createCluster = (coordinates, features) => new YMapMarker({ coordinates }, getClustererCircle(features.length).cloneNode(true));
        if (clusters) {
            const clustererLayer = map.children.find((clusterer) => clusterer._props.features);
            clustererLayer.update({ features: points });
        }
        else {
            addZoomButtons();
            const { YMapClusterer, clusterByGrid } = await ymaps3.import("@yandex/ymaps3-clusterer@0.0.1");
            clusterer = new YMapClusterer({
                method: clusterByGrid({ gridSize: 64 }),
                features: points,
                marker: createMarker,
                cluster: createCluster,
            });
            setClusters(clusterer);
            map.addChild(clusterer);
        }
    };
    const handleCreateMap = useCallback(async () => {
        await ymaps3.ready;
        const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapListener, YMapLayer, YMapFeatureDataSource } = ymaps3;
        const newMap = new YMap(mapRef.current, {
            location: {
                center: [Number(defaultLng), Number(defaultLat)],
                zoom: defaultZoom,
            },
        }, [
            new YMapDefaultSchemeLayer({ theme: "dark" }),
            new YMapDefaultFeaturesLayer({}),
            new YMapFeatureDataSource({ id: MARKER_SOURCE }),
            new YMapLayer({ source: MARKER_SOURCE, type: "markers" }),
            new YMapListener({
                onClick: clickHandler,
                onActionEnd: (object) => {
                    if (object.type === "scrollZoom") {
                        updateStoreElements(MARKER_SOURCE);
                        handleSelectStore("");
                    }
                    const debouncedLoadFlotiqStores = debounce(handleLoadFlotiqStores);
                    debouncedLoadFlotiqStores(yandexMapRef.current);
                },
            }),
        ]);
        setMap(newMap);
        yandexMapRef.current = newMap;
        handleSelectStore("");
        handleLoadFlotiqStores(yandexMapRef.current);
        inputRef.current?.addEventListener("input", handleAutoComplete);
    }, [defaultLat, defaultLng, defaultZoom, handleLoadFlotiqStores, handleSelectStore]);
    const clickHandler = (object) => {
        handleSelectStore(object?.entity._props.id);
    };
    const updateMapLocation = (location, yandexMap) => {
        (map || yandexMap)?.update({
            location: {
                ...location,
                duration: 400,
            },
        });
    };
    const handleInitMap = useCallback(() => {
        setIsMapLoading(true);
        const head = document.head;
        const script = document.createElement("script");
        script.src = mapProviderExternalJs;
        script.type = "text/javascript";
        script.onload = async () => {
            await handleCreateMap();
            setIsMapLoading(false);
        };
        script.onerror = () => {
            setIsMapError(true);
            setIsMapLoading(false);
        };
        head.appendChild(script);
    }, [handleCreateMap, mapProviderExternalJs]);
    const handleLoadUserMarker = (userLat, userLng) => {
        const { YMapMarker } = ymaps3;
        const coordinates = [userLng, userLat];
        const marker = new YMapMarker({ coordinates: coordinates }, getUserLocationMarker());
        if (isDesktopView) {
            const popup = new YMapMarker({ coordinates: coordinates }, getUserLocationPopup());
            map?.addChild(marker).addChild(popup);
        }
        else {
            map.addChild(marker);
        }
        updateMapLocation({ center: coordinates });
    };
    const handleInputSearchClick = async () => {
        setIsSearchResultPending(true);
        if (inputRef.current) {
            const searchValue = inputRef.current.value;
            messageBus.publish("tracking.event", {
                payload: {
                    event_name: StoreLocatorEvents.SearchClick,
                    additionalData: {
                        search_term: searchValue,
                    },
                },
            });
            if (searchValue) {
                const searchResponse = await ymaps3.search({ text: searchValue });
                const { geometry } = searchResponse[0];
                handleLoadFlotiqStores(yandexMapRef.current);
                updateMapLocation({ center: geometry.coordinates, zoom: 15 }, yandexMapRef.current);
            }
        }
        setIsSearchResultPending(false);
    };
    const handleAutoComplete = async () => {
        const searchValue = inputRef.current.value;
        if (searchValue && yandexMapRef.current) {
            const { bounds, center } = yandexMapRef.current;
            const suggestions = await ymaps3.suggest({
                text: searchValue,
                limit: 5,
                bounds: bounds,
                center: center,
            });
            if (suggestions) {
                createSuggestionItems(inputRef, suggestions, handleInputSearchClick);
            }
        }
    };
    useEffect(() => {
        if (!map) {
            handleInitMap();
        }
    }, [handleInitMap, map]);
    useEffect(() => {
        const defaultZoom = 15;
        if (activeStoreId) {
            const activeMarker = markers.find((marker) => marker.id === activeStoreId);
            handleLoadFlotiqStores(yandexMapRef.current);
            const currentZoom = yandexMapRef.current.zoom;
            const zoom = currentZoom > defaultZoom ? currentZoom : defaultZoom;
            if (activeMarker) {
                updateMapLocation({
                    center: [activeMarker.Location.lon, activeMarker.Location.lat],
                    zoom,
                });
            }
            setTimeout(() => updateStoreElements(MARKER_SOURCE, activeStoreId), 500);
        }
    }, [activeStoreId]);
    const handleClickOutside = (event) => {
        if (inputRef.current &&
            !(inputRef.current.contains(event.target) || event.target.classList.contains(`${styles.yandexDropdownItem}`))) {
            updateDropdownContainer();
        }
    };
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [inputRef]);
    const addZoomButtons = () => {
        const { YMapControls, YMapControlButton } = ymaps3;
        const plusSign = document.createElement("div");
        plusSign.innerHTML = "+";
        const zoomPlusButton = new YMapControlButton({
            element: plusSign,
            onClick: () => {
                handleLoadFlotiqStores(yandexMapRef.current);
                updateMapLocation({ zoom: map.zoom + 1 });
            },
        });
        const minusSign = document.createElement("div");
        minusSign.innerHTML = "&minus;";
        const zoomMinusButton = new YMapControlButton({
            element: minusSign,
            onClick: () => {
                handleLoadFlotiqStores(yandexMapRef.current);
                updateMapLocation({ zoom: map.zoom - 1 });
            },
        });
        map?.addChild(new YMapControls({ position: "left" }, [zoomPlusButton, zoomMinusButton]));
    };
    return {
        mapRef,
        inputRef,
        isMapLoading,
        isMapError,
        isSearchResultPending,
        handleInitMap,
        handleInputSearchClick,
        handleLoadMarkers,
        handleLoadUserMarker,
        isSearchVisible,
    };
};
//# sourceMappingURL=useYandexMapsStoreLocator.js.map